// Thin wrapper around Sentry. The dependency is loaded dynamically so the
// module remains test-importable (and tree-shakeable) before the Sentry
// package is installed, and so a build without VITE_SENTRY_DSN never pulls
// it into the bundle. Both exported functions are no-ops when the DSN is
// empty or undefined.

interface SentryEvent {
  user?: unknown;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  breadcrumbs?: unknown;
  request?: { cookies?: unknown };
}

interface SentryInitOptions {
  dsn: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  beforeSend: (event: SentryEvent) => SentryEvent | null;
}

interface SentryModule {
  init: (opts: SentryInitOptions) => void;
  captureException: (
    err: unknown,
    hint?: { extra?: Record<string, unknown> },
  ) => void;
}

let sentry: SentryModule | null = null;
let initStarted = false;

function readDsn(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (typeof dsn !== 'string') return undefined;
  const trimmed = dsn.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

// Drop anything that smells like free-text recovery content. Sentry is for
// exceptions only — note/text bodies must never leave the device.
const SUSPICIOUS_KEYS = [
  'note',
  'notes',
  'text',
  'content',
  'body',
  'wrong',
  'wrongs',
  'amend',
  'amends',
  'defect',
  'defects',
  'asset',
  'assets',
  'prayer',
  'name',
];

function scrubExtras(
  extras: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!extras) return undefined;
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(extras)) {
    const lower = key.toLowerCase();
    if (SUSPICIOUS_KEYS.some((needle) => lower.includes(needle))) continue;
    if (typeof value === 'string' && value.length > 200) continue;
    cleaned[key] = value;
  }
  return cleaned;
}

function beforeSend(event: SentryEvent): SentryEvent | null {
  // Anonymous-population app: never attach a user object.
  delete event.user;
  if (event.extra) {
    event.extra = scrubExtras(event.extra);
  }
  if (event.request?.cookies) {
    delete event.request.cookies;
  }
  return event;
}

export function initErrorReporting(): void {
  const dsn = readDsn();
  if (!dsn) return;
  if (initStarted) return;
  initStarted = true;
  // Dynamic import keeps @sentry/react out of the bundle when no DSN is
  // configured and lets tests load this module without the package present.
  // The specifier is widened to `string` so TypeScript skips static module
  // resolution — @sentry/react is an optional peer dependency that isn't
  // installed until the integration step.
  const sentryPackage = '@sentry/react' as string;
  void import(/* @vite-ignore */ sentryPackage)
    .then((mod: unknown) => {
      const sdk = mod as SentryModule;
      sdk.init({
        dsn,
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeSend,
      });
      sentry = sdk;
    })
    .catch((error: unknown) => {
      console.error('Failed to initialize error reporting:', error);
    });
}

export function reportError(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!sentry) return;
  const cleaned = scrubExtras(context);
  sentry.captureException(err, cleaned ? { extra: cleaned } : undefined);
}
